import {ApiError, Fetcher} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {Icon, IconName, IconSize, IconType, IIconFilters} from '@mintdata/mint-kit/api/assets/Icon';
import {IPromiseCancelable, PromiseUtil} from '@mintdata/mint-kit/impl/util/PromiseUtil';
import bind from 'bind-decorator';

interface IconsSnapshot
{
    icons:IconSnapshot[],
}

interface IconSnapshot
{
    name:string;
    category:string;
    size:string;
    availableSizes:string[];
    svg:string;
    tags:string[],
}

export class IconsController
{
    private _fetcher:Fetcher;

    private _icons:Icon[] = [];

    private _iconsMap:Map<IconName, Icon> = new Map();

    public _iconsLoaded:boolean = false;

    private _iconLoadingPromises:Map<IconName, Promise<any>> = new Map();

    private _iconsLoadingPromise:Promise<any> = null;

    public constructor()
    {
        this._fetcher = new Fetcher();
    }

    @bind
    public getIcons():IPromiseCancelable<Icon[]>
    {
        return PromiseUtil.makeCancelable(this._getIcons());
    }

    @bind
    public searchIcons(text:string, filters?:IIconFilters, icons?:Icon[]):IPromiseCancelable<Icon[]>
    {
        return PromiseUtil.makeCancelable(this._searchIcons(text, filters, icons));
    }

    @bind
    public getIcon(name:IconName):IPromiseCancelable<Icon>
    {
        return PromiseUtil.makeCancelable(this._getIcon(name));
    }

    @bind
    private async _getIcons():Promise<Icon[]>
    {
        if( this._iconsLoaded )
            return this._icons;

        if( !this._iconsLoadingPromise )
            this._iconsLoadingPromise = this._fetcher.request('get', ConfigUtil.cdnIconsFileUrl(), null);

        const response:IconsSnapshot = await this._iconsLoadingPromise;

        if( response instanceof ApiError )
            throw new Error(response.message);

        const icons:Map<IconName, Icon> = this._deserializeIconsSnapshot(response);

        this._icons = Array.from(icons.values());
        this._iconsMap = icons;
        this._iconsLoaded = true;
        this._iconsLoadingPromise = null;

        return this._icons;
    }

    @bind
    private async _searchIcons(text:string, filters?:IIconFilters, icons?:Icon[]):Promise<Icon[]>
    {
        const size:IconSize = filters ? filters.size : null;
        const type:IconType = filters ? filters.type : null;

        icons = icons || await this._getIcons();

        const terms = text.toLowerCase().split(' ');

        return icons.filter((icon:Icon) =>
        {
            return (!size || size === icon.size)
                && (!type || type === icon.type)
                && (
                    icon.name.indexOf(text) !== -1
                    || terms.every(term => icon.tags.some(tag => tag.indexOf(term) !== -1))
                );
        });
    }

    @bind
    private async _getIcon(name:IconName):Promise<Icon>
    {
        if( this._iconsMap.has(name) )
            return this._iconsMap.get(name);

        const url:string = this._buildIconUrl(name);

        let responsePromise:Promise<IconSnapshot> = null;

        if( this._iconLoadingPromises.has(name) )
            responsePromise = this._iconLoadingPromises.get(name);
        else
        {
            responsePromise = this._fetcher.request('get', url, null);
            this._iconLoadingPromises.set(name, responsePromise);
        }

        const response:IconSnapshot = await responsePromise;

        if( response instanceof ApiError )
            throw new Error(response.message);

        const icon:Icon = this._deserializeIconSnapshot(response);

        this._icons.push(icon);
        this._iconsMap.set(icon.name, icon);
        this._iconLoadingPromises.delete(name);

        return icon;
    }

    private _buildIconUrl(name:IconName):string
    {
        return `${ConfigUtil.cdnIconsDirUrl()}/${name}.json`;
    }

    private _deserializeIconsSnapshot(snapshot:IconsSnapshot):Map<IconName, Icon>
    {
        const result:Map<IconName, Icon> = new Map();

        snapshot.icons.forEach((iconSnapshot:IconSnapshot) =>
        {
            const icon:Icon = this._deserializeIconSnapshot(iconSnapshot);

            result.set(icon.name, icon);
        });

        return result;
    }

    private _deserializeIconSnapshot(snapshot:IconSnapshot):Icon
    {
        return new Icon(
            snapshot.name,
            snapshot.category as IconType,
            this._deserializeIconSize(snapshot.size),
            snapshot.availableSizes.map(this._deserializeIconSize),
            snapshot.svg,
            snapshot.tags
        );
    }

    @bind
    private _deserializeIconSize(size:string):IconSize
    {
        return Number(size.slice(0, -2)) as IconSize;
    }
}
